/**
 * Language Switcher Component
 * Dropdown för att välja språk (stödjer flera språk)
 */

import { useState, useEffect, useRef } from 'react';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { useAuth, useAuthStore } from '@/store/authStore';
import { userService } from '@/services/user/userService';
import { GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

type Language = 'sv' | 'en' | 'lt' | 'de';

interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
}

const languages: LanguageOption[] = [
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių' },
];

const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);


  // Stäng dropdown när man klickar utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentLanguage = languages.find((lang) => lang.code === language) || languages[0];

  const handleLanguageChange = async (newLanguage: Language) => {
    try {
      console.log('Changing language to:', newLanguage);
      // Uppdatera språk (detta triggar i18n.changeLanguage som automatiskt uppdaterar alla komponenter)
      await setLanguage(newLanguage);
      console.log('Language changed successfully');
      setIsOpen(false);

      // Spara på användaren om inloggad
      if (user) {
        try {
          await userService.updateLanguage(newLanguage);
          
          // Uppdatera user-objektet i store
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            useAuthStore.setState({
              user: {
                ...currentUser,
                preferred_language: newLanguage,
              },
            });
          }
        } catch (error: any) {
          console.error('Failed to save language preference:', error);
          // Fortsätt ändå - språket är redan ändrat lokalt
        }
      }
    } catch (error: any) {
      console.error('Failed to change language:', error);
      toast.error(t('common.changeLanguageError') + ': ' + error.message);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bgLight hover:bg-bgElevated transition-colors text-textDefault"
        title={t('common.selectLanguage')}
      >
        <GlobeAltIcon className="w-5 h-5" />
        <span className="text-sm font-medium">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-bgElevated border border-border rounded-lg shadow-lg z-[9999] overflow-hidden">
          <div className="py-1">
            {languages && languages.length > 0 ? (
              languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    language === lang.code
                      ? 'bg-bgPrimary text-textLight'
                      : 'text-textDefault hover:bg-bgLight'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{lang.nativeName}</div>
                      <div className="text-xs opacity-75">{lang.name}</div>
                    </div>
                    {language === lang.code && (
                      <span className="text-xs">✓</span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-textSecondary">{t('commonUi.noLanguages')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
